import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import styled from 'styled-components';
import { HELP_CENTER, ConnectFragment } from '@helpcenter/connect-fragment';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot } from 'view/common/element-root';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import type { connectElementFragment$key } from './__generated__/connectElementFragment.graphql';

const CONNECT_FRAGMENT_APP_ROOT = 'connect-fragment-app-root';
export interface ConnectElementViewerProps {
    connectElementRef: connectElementFragment$key;
}

export const ConnectElement: React.FC<ConnectElementViewerProps> = ({ connectElementRef }) => {
    di(useIsPageInEditMode, ConnectFragment);
    const [isPageInEditMode] = useIsPageInEditMode();
    const connectElementData = useFragment(
        graphql`
            fragment connectElementFragment on HelpLayoutConnectElement {
                connectElementPage
                connectElementType
            }
        `,
        connectElementRef
    );
    const { connectElementType, connectElementPage } = connectElementData;
    const isValidConnectElement =
        connectElementType && ['headerAndSubheaderPanels', 'footerPanels'].includes(connectElementType);

    React.useEffect(() => {
        if (!isValidConnectElement) {
            trackError('connect.element.type.invalid', { connectElementType }, 'Invalid Connect Element Type received');
        }
    }, [isValidConnectElement, connectElementType]);

    if (connectElementPage === HELP_CENTER) {
        if (connectElementType === 'headerAndSubheaderPanels') {
            return (
                <ElementRoot
                    removePadding
                    removeMargin={isIncreasedFlexibilityEnabled()}
                    customStyles={[maxWidthStyle]}
                >
                    <ConnectWrapper isPageInEditMode={isPageInEditMode}>
                        <ConnectFragment
                            page={connectElementPage}
                            location="headerPanels"
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
                            className={CONNECT_FRAGMENT_APP_ROOT}
                        />
                        <ConnectFragment
                            page={connectElementPage}
                            location="subheaderPanels"
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
                            className={CONNECT_FRAGMENT_APP_ROOT}
                        />
                    </ConnectWrapper>
                </ElementRoot>
            );
        }
        if (connectElementType === 'footerPanels') {
            return (
                <ElementRoot
                    removePadding
                    removeMargin={isIncreasedFlexibilityEnabled()}
                    customStyles={[maxWidthStyle]}
                >
                    <ConnectWrapper isPageInEditMode={isPageInEditMode}>
                        <ConnectFragment
                            page={connectElementPage}
                            location={connectElementType}
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
                            className={CONNECT_FRAGMENT_APP_ROOT}
                        />
                    </ConnectWrapper>
                </ElementRoot>
            );
        }
    }
    return null;
};

const maxWidthStyle = xcss({
    maxWidth: '840px',
});

// This style wrapper here is leveraging `styled-component` and not `@compiled/react` because, it needed to use nested selectors like child selectors
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const ConnectWrapper = styled.div<{ isPageInEditMode?: boolean }>`
    margin: auto;
    padding-left: ${token('space.600', '48px')};
    padding-right: ${token('space.600', '48px')};
    /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
    > * {
        padding-top: ${token('space.300', '24px')};
    }
    /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
    > :first-child {
        padding-top: ${isIncreasedFlexibilityEnabled() ? token('space.400', '32px') : token('space.300', '24px')};
    }
    /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
    > :last-child {
        padding-bottom: ${isIncreasedFlexibilityEnabled() ? token('space.400', '32px') : token('space.300', '24px')};
    }
    /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
    .connect-fragment-app-root {
        margin-top: 0;
    }
    ${({ isPageInEditMode }) => isPageInEditMode && `pointer-events: none;`}
`;
